import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, BookOpen, UserCheck, Award, AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react';
import { UserWithRole } from '@/pages/admin/types';
import { getAllAvailableCourses, bulkAssignCourses, CourseOption } from '@/hooks/auth/enhancedCourseService';
import { useSupabaseAuth } from '@/hooks/auth';
import { toast } from '@/components/ui/sonner';

interface EnhancedBulkUserActionsProps {
  users: UserWithRole[];
  selectedUsers: string[];
  onSelectedUsersChange: (userIds: string[]) => void;
  onBulkApprove: (userIds: string[]) => Promise<boolean>;
  onBulkRoleChange: (userIds: string[], role: string) => Promise<boolean>;
  onRefreshData: () => void;
}

const EnhancedBulkUserActions: React.FC<EnhancedBulkUserActionsProps> = ({
  users,
  selectedUsers,
  onSelectedUsersChange,
  onBulkApprove,
  onBulkRoleChange,
  onRefreshData
}) => {
  const { user } = useSupabaseAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [availableCourses, setAvailableCourses] = useState<CourseOption[]>([]);
  const [assignmentStatus, setAssignmentStatus] = useState<{
    success: boolean;
    message: string;
    successCount?: number;
    errorCount?: number;
    details?: string[];
  } | null>(null);

  // Load courses on component mount
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const courses = await getAllAvailableCourses();
        setAvailableCourses(courses);
      } catch (error) {
        console.error('Failed to load courses:', error);
        toast.error('Failed to load available courses');
      }
    };
    
    loadCourses();
  }, []);
  const selectedCount = selectedUsers.length;

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      onSelectedUsersChange([]);
    } else {
      onSelectedUsersChange(users.map(u => u.id));
    }
  };

  const handleCourseSelection = (courseId: string, checked: boolean) => {
    if (checked) {
      setSelectedCourses(prev => [...prev, courseId]);
    } else {
      setSelectedCourses(prev => prev.filter(id => id !== courseId));
    }
  };

  const handleBulkCourseAssign = async () => {
    if (!user?.id) {
      toast.error('Authentication required');
      return;
    }

    if (selectedUsers.length === 0) {
      toast.error('Please select users first');
      return;
    }

    if (selectedCourses.length === 0) {
      toast.error('Please select courses to assign');
      return;
    }

    setIsLoading(true);
    setAssignmentStatus(null);
    
    try {
      console.log('Starting enhanced bulk course assignment:', {
        selectedUsers: selectedUsers.length,
        selectedCourses: selectedCourses.length,
        assignedBy: user.id
      });

      const success = await bulkAssignCourses(selectedUsers, selectedCourses, user.id);
      
      if (success) {
        // Clear selections after successful assignment
        setSelectedCourses([]);
        onSelectedUsersChange([]);
        onRefreshData();
        
        setAssignmentStatus({
          success: true,
          message: `Successfully assigned ${selectedCourses.length} courses to ${selectedUsers.length} users`,
          successCount: selectedCourses.length * selectedUsers.length,
          errorCount: 0
        });

        toast.success(`Successfully assigned courses to users`);
      } else {
        setAssignmentStatus({
          success: false,
          message: 'Course assignment failed. Please check console for details.',
          errorCount: selectedCourses.length * selectedUsers.length
        });
      }
    } catch (error) {
      console.error('Error in bulk course assignment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      setAssignmentStatus({
        success: false,
        message: `Assignment failed: ${errorMessage}`,
        errorCount: selectedCourses.length * selectedUsers.length,
        details: [errorMessage]
      });
      
      toast.error('Failed to assign courses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkApproval = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users first');
      return;
    }

    setIsLoading(true);
    try {
      const success = await onBulkApprove(selectedUsers);
      if (success) {
        onSelectedUsersChange([]);
        onRefreshData();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkRoleUpdate = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users first');
      return;
    }

    if (!selectedRole) {
      toast.error('Please select a role');
      return;
    }

    setIsLoading(true);
    try {
      const success = await onBulkRoleChange(selectedUsers, selectedRole);
      if (success) {
        setSelectedRole('');
        onSelectedUsersChange([]);
        onRefreshData();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const coursesByType = availableCourses.reduce((acc, course) => {
    if (!acc[course.type]) acc[course.type] = [];
    acc[course.type].push(course);
    return acc;
  }, {} as Record<string, CourseOption[]>);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Enhanced Bulk User Actions
          {selectedCount > 0 && (
            <Badge variant="secondary">{selectedCount} selected</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* User Selection */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            className="flex items-center gap-2"
          >
            <Checkbox
              checked={selectedUsers.length === users.length && users.length > 0}
              className="pointer-events-none"
            />
            {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
          </Button>
          <span className="text-sm text-muted-foreground">
            {selectedCount} of {users.length} users selected
          </span>
        </div>

        {/* Enhanced Assignment Status */}
        {assignmentStatus && (
          <div className={`p-4 rounded-lg border ${
            assignmentStatus.success 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-start gap-2">
              {assignmentStatus.success ? (
                <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <div className="font-medium">{assignmentStatus.message}</div>
                {(assignmentStatus.successCount !== undefined || assignmentStatus.errorCount !== undefined) && (
                  <div className="text-sm mt-1">
                    {assignmentStatus.successCount !== undefined && (
                      <span>✓ Success: {assignmentStatus.successCount} assignments</span>
                    )}
                    {assignmentStatus.errorCount !== undefined && assignmentStatus.errorCount > 0 && (
                      <span className="ml-4">✗ Errors: {assignmentStatus.errorCount} failed</span>
                    )}
                  </div>
                )}
                {assignmentStatus.details && assignmentStatus.details.length > 0 && (
                  <div className="text-sm mt-2">
                    <details>
                      <summary className="cursor-pointer">View details</summary>
                      <ul className="mt-1 ml-4 list-disc">
                        {assignmentStatus.details.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul>
                    </details>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Course Assignment Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Assign Courses to Selected Users
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(coursesByType).map(([type, courses]) => (
              <div key={type} className="space-y-2">
                <h4 className="font-medium capitalize text-sm">{type} Courses</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto border rounded p-2">
                  {courses.map(course => (
                    <div key={course.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={course.id}
                        checked={selectedCourses.includes(course.id)}
                        onCheckedChange={(checked) => handleCourseSelection(course.id, !!checked)}
                      />
                      <label htmlFor={course.id} className="text-sm flex-1 cursor-pointer">
                        {course.title}
                        <Badge variant="outline" className="ml-2 text-xs">
                          {course.level}
                        </Badge>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {selectedCourses.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium">Selected courses:</span>
              {selectedCourses.map(courseId => {
                const course = availableCourses.find(c => c.id === courseId);
                return (
                  <Badge key={courseId} variant="secondary" className="text-xs">
                    {course?.title || courseId}
                  </Badge>
                );
              })}
            </div>
          )}

          <Button
            onClick={handleBulkCourseAssign}
            disabled={isLoading || selectedUsers.length === 0 || selectedCourses.length === 0}
            className="w-full"
          >
            <Award className="h-4 w-4 mr-2" />
            {isLoading ? 'Assigning...' : `Assign ${selectedCourses.length} Courses to ${selectedCount} Users`}
          </Button>
        </div>

        {/* Other Bulk Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Bulk Approve Users</label>
            <Button
              variant="outline"
              onClick={handleBulkApproval}
              disabled={isLoading || selectedUsers.length === 0}
              className="w-full"
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Approve {selectedCount} Users
            </Button>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Change Role</label>
            <div className="flex gap-2">
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={handleBulkRoleUpdate}
                disabled={isLoading || selectedUsers.length === 0 || !selectedRole}
              >
                Update
              </Button>
            </div>
          </div>
        </div>

        {/* Assignment Info */}
        {selectedUsers.length > 0 && selectedCourses.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 p-3 rounded-lg">
            <Info className="h-4 w-4" />
            <span>
              Ready to assign {selectedCourses.length} courses to {selectedUsers.length} users 
              ({selectedCourses.length * selectedUsers.length} total assignments)
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EnhancedBulkUserActions;
